import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import {
	AddRequest,
	UnitSeriesDetailResDTO,
} from '@/api/DTO/exercise/meditation.interface';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { ExerciseColor } from '@/api/enum/common.enum';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { useFileUploader } from '@/hooks/useFileUploader';
import { FileInfo } from '@/api/DTO/common.interface';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
	useAddMeditationMutation,
	useUpdateMeditationMutation,
} from '@/api/exercise/meditationApiSlice';

interface Props {
	type: FormType;
	details?: UnitSeriesDetailResDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function MeditationInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [addMeditation] = useAddMeditationMutation();
	const [updateMeditation] = useUpdateMeditationMutation();
	const { uploadFile } = useFileUploader();
	const [iconFile, setIconFile] = useState<FileInfo | null>(null);
	const [coverFile, setCoverFile] = useState<FileInfo | null>(null);
	const [mediaFile, setMediaFile] = useState<FileInfo | null>(null);
	const [videoFile, setVideoFile] = useState<FileInfo | null>(null);
	const [mediaVideoCoverFile, setMediaVideoCoverFile] =
		useState<FileInfo | null>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef2 = useRef<HTMLInputElement>(null);
	const imageInputRef3 = useRef<HTMLInputElement>(null);
	const imageInputRef4 = useRef<HTMLInputElement>(null);
	const imageInputRef5 = useRef<HTMLInputElement>(null);

	const colorList = [
		{ id: ExerciseColor.AQUA, name: t('meditation.AQUA') },
		{ id: ExerciseColor.ORANGE, name: t('meditation.ORANGE') },
		{ id: ExerciseColor.CORAL, name: t('meditation.CORAL') },
		{ id: ExerciseColor.PINK, name: t('meditation.PINK') },
		{ id: ExerciseColor.PURPLE, name: t('meditation.PURPLE') },
		{ id: ExerciseColor.LAVENDER, name: t('meditation.LAVENDER') },
		{ id: ExerciseColor.BLUE, name: t('meditation.BLUE') },
		// { id: ExerciseColor.CX, name: t('meditation.CX') },
		// { id: ExerciseColor.MTR, name: t('meditation.MTR') },
	];

	const mediaType = [
		{ id: 3, name: t('meditation.image') },
		{ id: 2, name: t('meditation.video') },
	];

	const defaultValues: DefaultValues<UnitSeriesDetailResDTO> = {
		contentZh: details?.contentZh || '',
		contentEn: details?.contentEn || '',
		mediaType: details?.mediaType || '',
		hiddenSearch: details?.hiddenSearch || false,
	};
	useEffect(() => {
		if (type == FormType.edit) {
			if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
			if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
			if (details?.subtitleEn)
				methods.setValue('subtitleEn', details.subtitleEn);
			if (details?.subtitleZh)
				methods.setValue('subtitleZh', details.subtitleZh);
			methods.setValue('color', String(details?.color) || '');
			if (details?.contentEn) methods.setValue('contentEn', details.contentEn);
			if (details?.contentZh) methods.setValue('contentZh', details.contentZh);
			if (details?.hasContent)
				methods.setValue('hasContent', details.hasContent);
			if (details?.mediaType) methods.setValue('mediaType', details.mediaType);
			if (details?.coverImage) setCoverFile(details?.coverImage);
			if (details?.icon) setIconFile(details?.icon);
			if (details?.mediaType == 2) {
				setVideoFile(details?.media);
				methods.setValue('videoFormFile', details.media?.name);
				setMediaVideoCoverFile(details?.introductionMediaVideoCover);
				methods.setValue('mediaVideoCoverFormField', details.media?.name || '');
			}
			if (details?.mediaType == 3) setMediaFile(details?.media);
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		// subtitleZh: Yup.string().required(t('errorMessage.required')),
		color: Yup.string().required(t('errorMessage.pleaseSelect')),
		mediaVideoCoverFormField: Yup.string()
			.nullable()
			.when('videoFormFile', {
				is: (videoFormFile: string) => videoFormFile,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
	});
	const methods = useForm<UnitSeriesDetailResDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const mediaVideoCoverField = useController({
		name: 'mediaVideoCoverFormField',
		control: methods.control,
	});

	const watchHasContent = methods.watch('hasContent');
	const watchMediaType = methods.watch('mediaType');

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<UnitSeriesDetailResDTO> = async (data) => {
		const obj: AddRequest = { ...data };
		if (!methods.getValues('hasContent')) obj.hasContent = false;
		if (iconFile) obj.icon = iconFile;
		if (coverFile) obj.coverImage = coverFile;
		if (methods.getValues('hasContent')) {
			obj.contentEn = methods.getValues('contentEn');
			obj.contentZh = methods.getValues('contentZh');
			if (methods.getValues('mediaType') == 2) {
				obj.mediaVideo = videoFile;
				obj.mediaVideoCover = mediaVideoCoverFile;
			} else {
				delete obj.mediaVideoCover;
			}
			if (methods.getValues('mediaType') == 3) {
				obj.mediaImage = mediaFile;
			}
		} else {
			obj.contentEn = '';
			obj.contentZh = '';
			delete obj.mediaType;
			delete obj.mediaImage;
			delete obj.mediaVideo;
			delete obj.mediaVideoCover;
		}
		const response =
			type == FormType.create
				? await addMeditation(obj)
				: await updateMeditation({ ...obj, id: details?.seriesId || '' });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createMeditationSuccess')
					: t('snackbar.editMeditationSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/exercise-manage/meditation-list');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/exercise-manage/meditation-list');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
		type: string
	) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			if (type == 'icon') {
				setIconFile(res);
			} else if (type == 'media') {
				setMediaFile(res);
			} else if (type == 'cover') {
				setCoverFile(res);
			} else if (type == 'video') {
				setVideoFile(res);
				methods.setValue('videoFormFile', res.name);
			} else if (type == 'introduction') {
				setMediaVideoCoverFile(res);
				methods.setValue('mediaVideoCoverFormField', res.name);
			}
		}
	};

	const handleDeleteFile = (type: string) => {
		if (type == 'icon') {
			setIconFile(null);
			imageInputRef.current && (imageInputRef.current.value = '');
		} else if (type == 'cover') {
			setCoverFile(null);
			imageInputRef2.current && (imageInputRef2.current.value = '');
		} else if (type == 'media') {
			setMediaFile(null);
			imageInputRef3.current && (imageInputRef3.current.value = '');
		} else if (type == 'video') {
			setVideoFile(null);
			methods.setValue('videoFormFile', '');
			imageInputRef4.current && (imageInputRef4.current.value = '');
		} else if (type == 'introduction') {
			setMediaVideoCoverFile(null);
			methods.setValue('mediaVideoCoverFormField', '');
			imageInputRef4.current && (imageInputRef4.current.value = '');
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('meditation.title')}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='subtitleZh'
											label={t('meditation.subTitle_')}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('meditation.title_')}
											maxLength={100}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='subtitleEn'
											label={t('meditation.subTitle_')}
											maxLength={100}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppDropDownMenu
									control={methods.control}
									name='color'
									label={t('meditation.color')}
									options={colorList}
									size='small'
								/>
							</Grid>
							{/* 隱藏於練習庫及搜尋欄 */}
							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.isHiddenPracticeAndSearch')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='hiddenSearch'
									row
									options={[
										{ id: true, name: t('common.yes') },
										{ id: false, name: t('common.no') },
									]}
								/>
							</Grid>
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.icon_')}
								</Typography>
								{iconFile && (
									<img
										src={iconFile?.url || ''}
										width={70}
										height={70}
										style={{
											objectFit: 'contain',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{iconFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event, 'icon');
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event, 'icon');
												}}
											/>
										</Button>
									)}
									{iconFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('icon');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
							</Grid>
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('meditation.coverImage_')}
								</Typography>
								{coverFile && (
									<img
										src={coverFile?.url || ''}
										width={390}
										height={138}
										style={{
											objectFit: 'contain',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{coverFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef2}
												onChange={(event) => {
													handleUpload(event, 'cover');
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef2}
												onChange={(event) => {
													handleUpload(event, 'cover');
												}}
											/>
										</Button>
									)}
									{coverFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile('cover');
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={12}>
								<SingleCheckbox
									control={methods.control}
									name='hasContent'
									label={t('meditation.hasContent')}
								/>
							</Grid>
							{watchHasContent && currentTab == 'zh' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.content')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentZh'
									/>
								</Grid>
							)}
							{watchHasContent && currentTab == 'en' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.content')}
									</Typography>
									<AppRichTextEditor
										control={methods.control}
										name='contentEn'
									/>
								</Grid>
							)}
							{watchHasContent && (
								<Grid item xs={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.mediaType_')}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='mediaType'
										row
										options={mediaType}
									/>
								</Grid>
							)}
							{watchHasContent && watchMediaType == 3 && (
								<Grid item xs={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('meditation.mediaImage_')}
									</Typography>
									{mediaFile && (
										<img
											src={mediaFile?.url || ''}
											width={390}
											height={138}
											style={{
												objectFit: 'contain',
											}}
										/>
									)}

									<Box display={'flex'} alignItems={'center'}>
										{mediaFile ? (
											<Button
												component='label'
												sx={{ height: 24 }}
												endIcon={<LoopOutlinedIcon />}
											>
												{t('information.changeImage')}
												<VisuallyHiddenInput
													type='file'
													name='thumbnail'
													accept='image/*'
													ref={imageInputRef3}
													onChange={(event) => {
														handleUpload(event, 'media');
													}}
												/>
											</Button>
										) : (
											<Button
												component='label'
												sx={{ height: 24 }}
												endIcon={<DriveFolderUploadOutlinedIcon />}
											>
												{t('information.uploadImage')}
												<VisuallyHiddenInput
													type='file'
													name='thumbnail'
													accept='image/*'
													ref={imageInputRef3}
													onChange={(event) => {
														handleUpload(event, 'media');
													}}
												/>
											</Button>
										)}
										{mediaFile && (
											<Button
												component='label'
												sx={{ height: 24, marginLeft: '24px' }}
												color='error'
												endIcon={<DeleteOutlinedIcon />}
												onClick={() => {
													handleDeleteFile('media');
												}}
											>
												{t('information.deleteImage')}
											</Button>
										)}
									</Box>
								</Grid>
							)}
							{watchHasContent && watchMediaType == 2 && (
								<>
									<Grid item xs={6}>
										<AppTextField
											disabled
											control={methods.control}
											name='videoFormFile'
											label={t('meditation.mediaVideo_')}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														{videoFile?.url && (
															<IconButton
																type='button'
																onClick={() => handleDeleteFile('video')}
															>
																<CloseIcon />
															</IconButton>
														)}

														<IconButton component='label'>
															<AttachFileIcon />
															<VisuallyHiddenInput
																type='file'
																accept='.mp4'
																onChange={(event) =>
																	handleUpload(event, 'video')
																}
																ref={imageInputRef4}
															/>
														</IconButton>
													</InputAdornment>
												),
											}}
										/>
									</Grid>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('day21.previewImage')}
										</Typography>
										{mediaVideoCoverFile && (
											<img
												src={mediaVideoCoverFile?.url || ''}
												width={390}
												height={138}
												style={{
													objectFit: 'contain',
												}}
											/>
										)}

										<Box display={'flex'} alignItems={'center'}>
											{mediaVideoCoverFile ? (
												<Button
													component='label'
													sx={{ height: 24 }}
													endIcon={<LoopOutlinedIcon />}
												>
													{t('information.changeImage')}
													<VisuallyHiddenInput
														type='file'
														name='thumbnail'
														accept='image/*'
														ref={imageInputRef5}
														onChange={(event) => {
															handleUpload(event, 'introduction');
														}}
													/>
												</Button>
											) : (
												<Button
													component='label'
													sx={{ height: 24 }}
													endIcon={<DriveFolderUploadOutlinedIcon />}
												>
													{t('information.uploadImage')}
													<VisuallyHiddenInput
														type='file'
														name='thumbnail'
														accept='image/*'
														ref={imageInputRef5}
														onChange={(event) => {
															handleUpload(event, 'introduction');
														}}
													/>
												</Button>
											)}
											{mediaVideoCoverFile && (
												<Button
													component='label'
													sx={{ height: 24, marginLeft: '24px' }}
													color='error'
													endIcon={<DeleteOutlinedIcon />}
													onClick={() => {
														handleDeleteFile('introduction');
													}}
												>
													{t('information.deleteImage')}
												</Button>
											)}
										</Box>
										{methods.getValues('videoFormFile') &&
											!methods.getValues('mediaVideoCoverFormField') && (
												<FormHelperText
													color='error'
													style={{ color: '#d32f2f' }}
												>
													{mediaVideoCoverField.fieldState.error?.message}
												</FormHelperText>
											)}
									</Grid>
								</>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
